import React, { useEffect, useState, useCallback, useMemo } from "react"
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { styled } from "@mui/system"
import TelegramIcon from "@mui/icons-material/Telegram"
import PlayCircleIcon from "@mui/icons-material/PlayCircle"
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye"
import SaveIcon from "@mui/icons-material/BookmarkAdd"
import CloseIcon from "@mui/icons-material/BookmarkRemove"
import ReplyAllIcon from '@mui/icons-material/ReplyAll'
import ImageSwpiderModal from "../swiper"
import ImageGallery from "./ImageGallery"
import { DOMAIN } from "../../config"
import { convertSecondsToTime, extractTag, getKeywords } from "../../funcs"
import { useUser } from "../../contexts/userContext"
import FollowButton from "../common/FollowButton"
import relativeTime from 'dayjs/plugin/relativeTime'
import dayjs from 'dayjs'

import "dayjs/locale/zh-cn"
import ShareSilverDialog from "../appDialog/shareSilverDialog"
import LoginRegisterDialog from "../appDialog/login"
dayjs.extend(relativeTime)
dayjs.locale("zh-cn")

const EyeIconWithCount = React.memo(({ count }) => (
  <Box display="flex" alignItems="center">
    <RemoveRedEyeIcon sx={{ fontSize: 16, color: "#999" }} />
    <Typography variant="body2" sx={{ ml: 0.5, color: "#999" }}>
      {count}
    </Typography>
  </Box>
))

const CollapsibleText = styled(Typography)(({ expanded }) => ({
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  WebkitLineClamp: expanded ? "none" : 3,
  color: "#666",
  marginBottom: 10,
  cursor: "pointer",
  fontFamily: `"Source Code Pro", "Courier New", monospace`,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  letterSpacing: "0.15em",
}))

const computeImageLayout = (video, isMobile, hasTitle) => {
  const count = video.medias.length
  let imgColumn = 3
  let imgHeight = isMobile ? 105 : 165

  if (count === 1) {
    imgHeight = isMobile ? 250 : 400
    imgColumn = 1
  } else if (!hasTitle && !isMobile) {
    imgColumn = count <= 4 ? 2 : count <= 6 ? 3 : 4
  } else if (hasTitle && !isMobile) {
    imgColumn = 8
  } else if (isMobile) {
    imgColumn = count <= 4 ? 2 : 3
  }

  return { imgColumn, imgHeight }
}

const VideoAvatar = React.memo(({ video, DOMAIN }) => {
  const medias = video.medias || []
  const last = medias[medias.length - 1]
  const secondLast = medias[medias.length - 2]

  const fallbackImages = useMemo(() => [
    `${DOMAIN}miniapp/${last?.fileid}.tb.jpg`,
    `${DOMAIN}miniapp/${secondLast?.fileid}.tb.jpg`,
    `${DOMAIN}miniapp/${last?.fileid}.jpg`,
    `${DOMAIN}miniapp/${secondLast?.fileid}.jpg`,
  ].filter(Boolean), [DOMAIN, last, secondLast])

  const [imgSrc, setImgSrc] = useState(fallbackImages[0])
  const [fallbackIndex, setFallbackIndex] = useState(0)

  const handleError = useCallback(() => {
    const nextIndex = fallbackIndex + 1
    if (nextIndex < fallbackImages.length) {
      setImgSrc(fallbackImages[nextIndex])
      setFallbackIndex(nextIndex)
    } else {
      setImgSrc(null)
    }
  }, [fallbackIndex, fallbackImages])

  return (
    <Avatar
      src={imgSrc || undefined}
      onError={handleError}
      sx={{
        background: "linear-gradient(135deg, #a18cd1, #fbc2eb)",
        color: "#fff",
        fontWeight: 600,
      }}
    >
      {!imgSrc && (video.title?.[0] || "系")}
    </Avatar>
  )
})

const FeedCardList = React.memo(function FeedCardList({
  handleScroll,
  latest,
  setLatest,
  setAppInfoMessage,
  setAppInfoOpen,
  matches,
  video,
  setOpenSearch,
  setChipTitle,
  remark,
  title,
  swiper,
  setSwiperLock,
  player,
  agent,
  user,
  openPlayerGlobal,
  setPlayerThumbnail,
  setPlayerVideoId,
}) {
  const [open, setOpen] = useState(false)
  const [openShareDialog, setOpenShareDialog] = useState(false)
  const [imgIndex, setImgIndex] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"))
  const { userData } = useUser()
  const [openLogin, setOpenLogin] = useState(false)


  const autor = useMemo(() => extractTag(video.caption), [video.caption])
  const { imgColumn, imgHeight } = useMemo(() => computeImageLayout(video, isMobile, !!title), [video, isMobile, title])

  
  
  const handleOpenImageModal = useCallback((index) => {
    setImgIndex(index)
    setOpen(true)
  }, [])

  const handlePlay = useCallback(() => {
    const type = video.type === 11 ? "tv" : "vid"
    const url = `${player}?start=${type}_${video.id}${agent}`
    const isEmptyUser = !user || Object.keys(user).length === 0
    isEmptyUser
      ? window.open(url, "_blank")
      : window.Telegram.WebApp.openTelegramLink(url)
  }, [video, player, agent, user])

  const handleSearch = useCallback((keyword) => {
  if (!keyword) return

  // 去掉零宽空格
  const cleanedKeyword = keyword.replace(/\u200B/g, "")

  if (cleanedKeyword !== title) {
    setChipTitle(cleanedKeyword)
    setOpenSearch(true)
  }
}, [title, setChipTitle, setOpenSearch])


  const handleSave = useCallback(() => {
    const saveWeb = (userData, videoid) => {
      const api =
        title === "我的收藏"
          ? `${DOMAIN}ywyminiappbot/deleteFavorWeb?username=${userData.username}&vid=${videoid}&cookieid=${userData.cookie}`
          : `${DOMAIN}ywyminiappbot/addFavorWeb?username=${userData.username}&vid=${videoid}&cookieid=${userData.cookie}`

      fetch(api, { credentials: "include" })
        .then((res) => res.json())
        .then(() => {
          if (title === "我的收藏") {
            setLatest((prev) => prev.filter((v) => v.id !== videoid))
            handleScroll?.()
          } else {
            setAppInfoOpen(true)
          }
        })
    }

    if (!user || Object.keys(user).length === 0) {
      if (userData) {
        saveWeb(userData, video.id)
        setAppInfoMessage("收藏成功")
        setAppInfoOpen(true)
      } else {
        setAppInfoMessage("请先登录")
        setAppInfoOpen(true)
      }
      return
    }

    const api =
      title === "我的收藏"
        ? `${DOMAIN}ywyminiappbot/deleteFavor?userid=${user.user.id}&vid=${video.id}`
        : `${DOMAIN}ywyminiappbot/addFavor?userid=${user.user.id}&vid=${video.id}`

    fetch(api)
      .then((res) => res.json())
      .then(() => {
        if (title === "我的收藏") {
          setLatest((prev) => prev.filter((v) => v.id !== video.id))
          handleScroll?.()
        } else {
          setAppInfoOpen(true)
        }
      })
  }, [user, userData, video.id, title, setLatest, setAppInfoMessage, setAppInfoOpen, handleScroll])

  const keywords = useMemo(() => remark ? getKeywords(video.caption) : [], [video.caption, remark])



  return (
    <Box>
      <ImageSwpiderModal
        matches={matches}
        setSwiperLock={setSwiperLock}
        open={open}
        setOpen={setOpen}
        imgIndex={imgIndex}
        setImgIndex={setImgIndex}
        medias={video.medias}
      />
      <ShareSilverDialog open={openShareDialog} onClose={() =>  setOpenShareDialog(false)  } video={video} />
      <LoginRegisterDialog open={openLogin} onClose={() => setOpenLogin(false)} />
      
      <Card sx={{
        m: 1,
        borderRadius: "20px",
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(12px)",
        boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
          transform: "translateY(-2px)",
        },
      }}>
        <CardHeader
          onClick={() => handleSearch(autor)}
          avatar={<VideoAvatar video={video} DOMAIN={DOMAIN} />}
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, fontSize: 15, color: "#222" }}
              >
                {autor}
              </Typography>
              <Chip
                label={video.vip ? "VIP" : "免费"}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 500,
                  borderRadius: "6px",
                  background: video.vip
                    ? "linear-gradient(90deg, #ff6a00, #ee0979)"
                    : "linear-gradient(90deg, #56ab2f, #a8e063)",
                  color: "#fff",
                  px: 0.5,
                }}
              />
            </Box>
          }
          subheaderTypographyProps={{ sx: { fontSize: 12, color: "#888" } }}
          subheader={`${dayjs(video.date * 1000).fromNow()}更新${video.duration ? ` · 视频时长 ${convertSecondsToTime(video.duration)}` : ""
            }`}
          action={
            autor.includes("\u200B") && (
              <FollowButton
                bloger={autor}
                onChange={(newState) => {
                  setLatest((prev) =>
                    prev.map((v) =>
                      v.blogerId === video.blogerId ? { ...v, isFollowed: newState } : v
                    )
                  )
                }}
              />
            )
          }

        />


        <Box px={isMobile ? 1.5 : 4}>
          <ImageGallery
            video={video}
            imgColumn={imgColumn}
            imgHeight={imgHeight}
            openHandle={handleOpenImageModal}
            DOMAIN={DOMAIN}
            sx={{
              borderRadius: "14px",
              overflow: "hidden",
              "& img": { borderRadius: "14px", transition: "transform 0.3s ease" },
              "& img:hover": { transform: "scale(1.03)" },
            }}
          />
        </Box>

        <CardContent sx={{ pb: 0 }}>
          <CollapsibleText
            variant="body2"
            expanded={isExpanded ? 1 : 0}

            sx={{ fontSize: 14, lineHeight: 1.6, color: "#444", "&:hover": { color: "#000" } }}
          >
            {video.caption}
          </CollapsibleText>

          <Box display="flex" flexWrap="nowrap" gap={1} mt={1} sx={{
            overflowX: "auto", overflowY: "hidden", whiteSpace: "nowrap", paddingBottom: 2,
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.2)", borderRadius: 3 },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          }}>
            {keywords.map((kw, i) => (
              <Chip
                key={`${video.id}-${i}`}
                label={kw}
                size="small"
                onClick={() => handleSearch(kw)}
                sx={{
                  borderRadius: "14px",
                  fontSize: 12,
                  fontWeight: 500,
                  px: 1,
                  flexShrink: 0,
                  background: kw === title ? "linear-gradient(90deg, #a18cd1, #fbc2eb)" : "transparent",
                  border: kw === title ? "none" : "1px solid rgba(161,140,209,0.4)",
                  color: kw === title ? "#fff" : "#a18cd1",
                  "&:hover": { background: "linear-gradient(90deg, #a18cd1, #fbc2eb)", color: "#fff" },
                }}
              />
            ))}
          </Box>
        </CardContent>

        <CardActions sx={{ px: 2, pb: 0 }}>
          <IconButton><EyeIconWithCount count={video.views + 1} /></IconButton>
          <IconButton onClick={handleSave}>
            {title === "我的收藏" ? <CloseIcon sx={{ color: "#a18cd1" }} /> : <SaveIcon sx={{ color: "#a18cd1" }} />}
          </IconButton>
          <IconButton onClick={() => {
            if(userData){
              setOpenShareDialog(true)
            } else {
              setOpenLogin(true)
            }
            
          }}>
            <ReplyAllIcon sx={{ color: "#a18cd1" }}/>
          </IconButton>
        </CardActions>

        <CardActions sx={{ px: 2, pb: 2 }}>
          <Box sx={{ width: "100%", textAlign: "right" }}>
            
            {video.r2 !== 0 && video.r2 !== null && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PlayCircleIcon sx={{ fontSize: 16 }} />}
                onClick={() => { 
                  setPlayerVideoId(video.id) 
                  openPlayerGlobal(true) 
                  setPlayerThumbnail(video.medias[0].fileid) 
                }}
                sx={{
                  mr: 1, borderRadius: "16px", textTransform: "none", fontSize: 12, fontWeight: 500, px: 2, py: 0.6,
                  background: "linear-gradient(135deg, #e0f0ff, #ffffff)", color: "#007aff", boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  transition: "all 0.25s ease",
                  "&:hover": { transform: "translateY(-1px) scale(1.03)", boxShadow: "0 4px 12px rgba(0,0,0,0.12)", background: "linear-gradient(135deg, #cce8ff, #f0f9ff)" },
                  "&:active": { transform: "translateY(1px) scale(0.97)", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                }}
              >在线播放</Button>
            )} 

            <Button
              variant="contained"
              size="small"
              startIcon={<TelegramIcon sx={{ fontSize: 16 }} />}
              onClick={handlePlay}
              sx={{
                borderRadius: "16px", textTransform: "none", fontSize: 12, fontWeight: 500, px: 2, py: 0.6,
                background: "linear-gradient(135deg, #f0f9ff, #ffffff)", color: "#007aff", boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                transition: "all 0.25s ease",
                "&:hover": { transform: "translateY(-1px) scale(1.03)", boxShadow: "0 4px 12px rgba(0,0,0,0.12)", background: "linear-gradient(135deg, #d9f0ff, #f7fbff)" },
                "&:active": { transform: "translateY(1px) scale(0.97)", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
              }}
            >电报播放</Button>
          </Box>
        </CardActions>
      </Card>
    </Box>
  )
})

export default FeedCardList
